import { Link } from "react-router";
import { Radar, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/states";
import { formatDateBR } from "@/utils/dateUtils";
import { useAgenciaFreelancerRelacao } from "@/hooks/useAgenciaFreelancerRelacao";

interface AgenciaFreelancersTabProps {
  agenciaId: string;
}

/**
 * Aba "Freelancers": todo freelancer que já se cruzou com a agência
 * (candidatura, projeto, avaliação ou radar), com o resumo da relação
 * (v_agencia_freelancer_relacao, sql/150).
 */
export function AgenciaFreelancersTab({ agenciaId }: AgenciaFreelancersTabProps) {
  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAgenciaFreelancerRelacao(agenciaId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-2 py-6 text-center">
        <p className="text-sm text-muted-foreground">Não foi possível carregar os freelancers.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Tentar de novo
        </Button>
      </div>
    );
  }

  const linhas = data?.pages.flatMap((pagina) => pagina.linhas) ?? [];

  if (!linhas.length) {
    return (
      <EmptyState
        icon={Users}
        title="Nenhum freelancer relacionado ainda"
        description="Quando alguém candidatar, trabalhar ou for avaliado por essa agência, aparece aqui."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">Freelancer</th>
              <th className="px-3 py-2 font-medium">Candidaturas</th>
              <th className="px-3 py-2 font-medium">Aprovações</th>
              <th className="px-3 py-2 font-medium">Preterido</th>
              <th className="px-3 py-2 font-medium">Projetos juntos</th>
              <th className="px-3 py-2 font-medium">Período</th>
              <th className="px-3 py-2 font-medium">Avaliação dada</th>
              <th className="px-3 py-2 font-medium">Radar</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((linha) => (
              <tr key={linha.freelancer_id} className="border-b last:border-b-0">
                <td className="px-3 py-2 font-medium">
                  <Link
                    to={`/freelancers/${linha.freelancer_id}`}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {linha.freelancer_nome}
                  </Link>
                </td>
                <td className="px-3 py-2 tabular-nums">{linha.vagas_como_candidato}</td>
                <td className="px-3 py-2 tabular-nums">{linha.vezes_aprovado}</td>
                <td className="px-3 py-2 tabular-nums">{linha.vezes_preterido}</td>
                <td className="px-3 py-2 tabular-nums">{linha.projetos_juntos}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {linha.periodo_inicio
                    ? `${formatDateBR(linha.periodo_inicio)} até ${
                        linha.periodo_fim ? formatDateBR(linha.periodo_fim) : "hoje"
                      }`
                    : "sem período registrado"}
                </td>
                <td className="px-3 py-2 tabular-nums">
                  {linha.media_avaliacao_dada !== null ? (
                    <span>
                      {linha.media_avaliacao_dada}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({linha.avaliacoes_dadas_count})
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">sem avaliação</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {linha.indicacao_radar_ativa && (
                    <Badge variant="outline" className="gap-1">
                      <Radar className="h-3 w-3" />
                      Indicado
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Carregando..." : "Mostrar mais"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default AgenciaFreelancersTab;
